import { supabase } from '../lib/supabase';

// Convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj) return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  if (typeof obj !== 'object') return obj;

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

// Convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (!obj) return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  if (typeof obj !== 'object') return obj;

  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

export const blogService = {
  async getPublishedBlogs() {
    const { data, error } = await supabase?.from('blogs')?.select(`
        *,
        author:user_profiles(id, full_name, avatar_url)
      `)?.eq('status', 'published')?.order('published_date', { ascending: false });

    if (error) throw error;
    return toCamelCase(data);
  },

  async getBlogById(id) {
    const { data, error } = await supabase?.from('blogs')?.select(`
        *,
        author:user_profiles(id, full_name, avatar_url)
      `)?.eq('id', id)?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async getAllBlogs() {
    const { data, error } = await supabase?.from('blogs')?.select(`
        *,
        author:user_profiles(id, full_name, avatar_url)
      `)?.order('created_at', { ascending: false });

    if (error) throw error;
    return toCamelCase(data);
  },

  async createBlog(blogData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const snakeData = toSnakeCase({
      ...blogData,
      authorId: user?.id,
      publishedDate: blogData?.status === 'published' ? new Date()?.toISOString() : null
    });

    const { data, error } = await supabase?.from('blogs')?.insert(snakeData)?.select()?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async updateBlog(id, blogData) {
    const snakeData = toSnakeCase(blogData);

    const { data, error } = await supabase?.from('blogs')?.update(snakeData)?.eq('id', id)?.select()?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async deleteBlog(id) {
    const { error } = await supabase?.from('blogs')?.delete()?.eq('id', id);

    if (error) throw error;
  },

  async getRelatedBlogs(currentBlogId, category, limit = 3) {
    const { data, error } = await supabase?.from('blogs')?.select(`
        *,
        author:user_profiles(id, full_name, avatar_url)
      `)
      ?.eq('status', 'published')
      ?.eq('category', category)
      ?.neq('id', currentBlogId)
      ?.order('popularity', { ascending: false })
      ?.limit(limit);

    if (error) throw error;
    return toCamelCase(data);
  },

  async incrementPopularity(id) {
    const { data: blog } = await supabase?.from('blogs')?.select('popularity')?.eq('id', id)?.single();
    
    const newPopularity = (blog?.popularity || 0) + 1;
    
    const { error } = await supabase?.from('blogs')?.update({ popularity: newPopularity })?.eq('id', id);

    if (error) throw error;
  },

  async subscribeToNewsletter(email) {
    // First, check if the email already exists
    const { data: existingSubscriber, error: checkError } = await supabase
      ?.from('newsletter_subscribers')
      ?.select('*')
      ?.eq('email', email)
      ?.single();

    // If subscriber exists
    if (existingSubscriber) {
      // If already active, return a friendly message
      if (existingSubscriber?.is_active) {
        return { 
          ...toCamelCase(existingSubscriber), 
          alreadySubscribed: true,
          message: 'You are already subscribed to our newsletter!' 
        };
      }
      
      // If inactive, reactivate the subscription
      const { data: reactivatedData, error: updateError } = await supabase
        ?.from('newsletter_subscribers')
        ?.update({ is_active: true, subscribed_at: new Date()?.toISOString() })
        ?.eq('email', email)
        ?.select()
        ?.single();

      if (updateError) throw updateError;
      
      return { 
        ...toCamelCase(reactivatedData), 
        reactivated: true,
        message: 'Welcome back! Your subscription has been reactivated.' 
      };
    }

    // New subscriber - insert
    const { data, error } = await supabase
      ?.from('newsletter_subscribers')
      ?.insert({ email, is_active: true })
      ?.select()
      ?.single();

    if (error) throw error;
    
    return { 
      ...toCamelCase(data), 
      message: 'Successfully subscribed! Check your email for confirmation.' 
    };
  },

  async getAllSubscribers() {
    const { data, error } = await supabase?.from('newsletter_subscribers')?.select('*')?.eq('is_active', true)?.order('subscribed_at', { ascending: false });

    if (error) throw error;
    return toCamelCase(data);
  },

  async unsubscribeFromNewsletter(id) {
    const { error } = await supabase?.from('newsletter_subscribers')?.update({ is_active: false, unsubscribed_at: new Date()?.toISOString() })?.eq('id', id);

    if (error) throw error;
  },

  async sendNewsletterEmail(blogId) {
    const blog = await this.getBlogById(blogId);
    const subscribers = await this.getAllSubscribers();

    if (subscribers?.length === 0) {
      throw new Error('No active subscribers found');
    }

    const blogUrl = `${window.location?.origin}/blog-posts/${blogId}`;
    const subscriberEmails = subscribers?.map(sub => sub?.email);

    const response = await fetch(`${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-newsletter`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({
        blogTitle: blog?.title,
        blogExcerpt: blog?.excerpt,
        blogUrl,
        subscriberEmails
      })
    });

    if (!response?.ok) {
      const errorData = await response?.json();
      throw new Error(errorData?.error || 'Failed to send newsletter');
    }

    return await response?.json();
  },

  async publishScheduledBlogs() {
    const { error } = await supabase?.rpc('publish_scheduled_blogs');
    if (error) throw error;
  }
};
